import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import i18n from '@/shared/lib/i18n';

// Types
export interface UploadProgress {
    id: string;
    fileName: string;
    fileSize: number;
    tableName: string;
    projectId: string;
    progress: number;
    status: 'pending' | 'uploading' | 'processing' | 'annotating' | 'success' | 'error';
    message: string;
    logs: string[];
    startedAt: Date;
    completedAt?: Date;
    result?: {
        table_name?: string;
        row_count?: number;
        upload_id?: number;
    };
    error?: string;
}

interface UploadContextValue {
    // State
    uploads: UploadProgress[];
    activeUpload: UploadProgress | null;

    // Actions
    startUpload: (upload: Omit<UploadProgress, 'id' | 'startedAt' | 'logs' | 'status' | 'progress' | 'message'>) => string;
    updateUpload: (id: string, updates: Partial<UploadProgress>) => void;
    addLog: (id: string, log: string) => void;
    completeUpload: (id: string, result?: UploadProgress['result']) => void;
    failUpload: (id: string, error: string) => void;
    clearUpload: (id: string) => void;
    clearAllCompleted: () => void;

    // UI State
    isMinimized: boolean;
    setIsMinimized: (minimized: boolean) => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function useUpload() {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
}

// Optional hook that doesn't throw - for components that may not need upload
export function useUploadOptional() {
    return useContext(UploadContext);
}

interface UploadProviderProps {
    children: ReactNode;
}

export function UploadProvider({ children }: UploadProviderProps) {
    const [uploads, setUploads] = useState<UploadProgress[]>([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const idCounterRef = useRef(0);

    const startUpload = useCallback((upload: Omit<UploadProgress, 'id' | 'startedAt' | 'logs' | 'status' | 'progress' | 'message'>) => {
        const id = `upload_${Date.now()}_${++idCounterRef.current}`;
        const newUpload: UploadProgress = {
            ...upload,
            id,
            status: 'pending',
            progress: 0,
            message: i18n.t('shared:uploadContext.starting'),
            logs: [],
            startedAt: new Date(),
        };

        setUploads(prev => [...prev, newUpload]);
        return id;
    }, []);

    const updateUpload = useCallback((id: string, updates: Partial<UploadProgress>) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? { ...u, ...updates } : u
        ));
    }, []);

    const addLog = useCallback((id: string, log: string) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? { ...u, logs: [...u.logs, log] } : u
        ));
    }, []);

    const completeUpload = useCallback((id: string, result?: UploadProgress['result']) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? {
                ...u,
                status: 'success',
                progress: 100,
                message: i18n.t('shared:uploadContext.completed'),
                completedAt: new Date(),
                result
            } : u
        ));

        // Auto-clear after 3 seconds
        setTimeout(() => {
            setUploads(prev => prev.filter(u => u.id !== id));
        }, 3000);
    }, []);

    const failUpload = useCallback((id: string, error: string) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? {
                ...u,
                status: 'error',
                message: i18n.t('shared:uploadContext.error', { error }),
                error,
                completedAt: new Date()
            } : u
        ));
    }, []);

    const clearUpload = useCallback((id: string) => {
        setUploads(prev => prev.filter(u => u.id !== id));
    }, []);

    const clearAllCompleted = useCallback(() => {
        setUploads(prev => prev.filter(u => u.status !== 'success' && u.status !== 'error'));
    }, []);

    const activeUpload = uploads.find(u =>
        u.status === 'uploading' || u.status === 'processing' || u.status === 'annotating'
    ) || null;

    const value: UploadContextValue = {
        uploads,
        activeUpload,
        startUpload,
        updateUpload,
        addLog,
        completeUpload,
        failUpload,
        clearUpload,
        clearAllCompleted,
        isMinimized,
        setIsMinimized,
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
}

export default UploadContext;
